import React, { useState, useEffect } from 'react';
import { adminService } from '../../../services/adminService';
import { extractList, extractMeta } from '../../../services/api';
import { Loading, EmptyState } from '../../../shared/components';
import './AdminCommonStyles.scss';

const StatusBadge = ({ value }) => (
  <span className={`status-badge status-badge--${value === 'active' ? 'active' : 'inactive'}`}>
    {value === 'active' ? 'Aktif' : 'Nonaktif'}
  </span>
);

const CompaniesPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({});
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [toast, setToast] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    email: '',
    phone: '',
    address: '',
    url_tema: '',
    tema_api_key: '',
    status: 'active',
  });

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  useEffect(() => {
    fetchCompanies();
  }, [page]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await adminService.getCompanies({ page, limit: 10 });
      setCompanies(extractList(res.data));
      setPageInfo(extractMeta(res.data));
    } catch (err) {
      showToast('Gagal memuat perusahaan');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingId(null);
    setFormData({
      name: '', code: '', email: '', phone: '',
      address: '', url_tema: '', tema_api_key: '', status: 'active',
    });
    setShowForm(true);
  };

  const handleEdit = async (id) => {
    const existing = companies.find((c) => c.company_id === id);
    const res = await adminService.getCompanyById(id);
    if (res.status) {
      setFormData({
        ...res.data,
        tema_api_key: res.data.tema_api_key ?? existing?.tema_api_key ?? '',
      });
      setEditingId(id);
      setShowForm(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.code) {
      showToast('Nama dan kode harus diisi');
      return;
    }
    try {
      const res = editingId
        ? await adminService.updateCompany(editingId, formData)
        : await adminService.createCompany(formData);
      if (res.status) {
        fetchCompanies();
        setShowForm(false);
      } else {
        showToast(res.message || 'Gagal menyimpan perusahaan');
      }
    } catch (err) {
      showToast('Gagal menyimpan perusahaan');
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Nonaktifkan perusahaan "${name}"?`)) {
      try {
        const res = await adminService.updateCompany(id, { status: 'inactive' });
        if (res.status) fetchCompanies();
        else showToast(res.message || 'Gagal menonaktifkan perusahaan');
      } catch (err) {
        showToast('Gagal menonaktifkan perusahaan');
      }
    }
  };

  const filtered = companies.filter((c) => {
    if (filterStatus && c.status !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !c.name?.toLowerCase().includes(q) &&
        !c.code?.toLowerCase().includes(q) &&
        !c.email?.toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  if (loading) return <Loading />;

  return (
    <div className="page-container">
      {toast && <div className="toast">{toast}</div>}
      <div className="page-header">
        <h1>Manajemen Perusahaan</h1>
        <p>Superadmin - Kelola semua perusahaan di sistem</p>
      </div>

      <div className="page-controls" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
        <div className="search-icon-wrap">
          <svg className="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Cari nama, kode, atau email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="filter-select"
        >
          <option value="">Semua Status</option>
          <option value="active">Aktif</option>
          <option value="inactive">Nonaktif</option>
        </select>
        <button className="btn-primary" onClick={handleAdd} style={{ boxShadow: '0 4px 12px rgba(18,183,173,0.3)', whiteSpace: 'nowrap' }}>
          + Tambah Perusahaan
        </button>
      </div>

      {filtered.length === 0 ? (
        <EmptyState message="Tidak ada perusahaan" />
      ) : (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nama</th>
                <th>Kode</th>
                <th>Email</th>
                <th>Telepon</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((company) => (
                <tr key={company.company_id}>
                  <td><strong>{company.name}</strong></td>
                  <td><span className="uniform-code">{company.code}</span></td>
                  <td className="text-muted">{company.email || '—'}</td>
                  <td className="text-muted">{company.phone || '—'}</td>
                  <td><StatusBadge value={company.status} /></td>
                  <td>
                    <button className="btn-sm btn-info" onClick={() => handleEdit(company.company_id)}>Edit</button>
                    <button className="btn-sm btn-danger" onClick={() => handleDelete(company.company_id, company.name)}>Hapus</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pageInfo.total > 10 && (
        <div className="pagination">
          <button onClick={() => setPage(page - 1)} disabled={page === 1}>← Sebelumnya</button>
          <span>Hal {page} / {pageInfo.last_page}</span>
          <button onClick={() => setPage(page + 1)} disabled={page === pageInfo.last_page}>Berikutnya →</button>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingId ? 'Edit Perusahaan' : 'Tambah Perusahaan Baru'}</h2>
            <form onSubmit={handleSubmit}>

              <div className="form-section-label">Informasi Utama</div>

              <div className="form-row">
                <div className="form-group">
                  <label>Nama Perusahaan *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="PT Contoh Makmur"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Kode *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="PCM"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email ?? ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="info@perusahaan.com"
                  />
                </div>
                <div className="form-group">
                  <label>Telepon</label>
                  <input
                    type="tel"
                    value={formData.phone ?? ''}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="021xxxxxxxx"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Alamat</label>
                <textarea
                  value={formData.address ?? ''}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Jl. Sudirman No. 1, Jakarta"
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                  <option value="active">Aktif</option>
                  <option value="inactive">Nonaktif</option>
                </select>
              </div>

              <div className="form-section-label">Integrasi TEMA</div>

              <div className="form-group">
                <label>URL TEMA</label>
                <input
                  type="url"
                  value={formData.url_tema ?? ''}
                  onChange={(e) => setFormData({ ...formData, url_tema: e.target.value })}
                  placeholder="https://tema.perusahaan.com"
                />
              </div>
              <div className="form-group">
                <label>API Key TEMA</label>
                <input
                  type="text"
                  value={formData.tema_api_key ?? ''}
                  onChange={(e) => setFormData({ ...formData, tema_api_key: e.target.value })}
                  placeholder="api_key_xxxxx"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">Simpan</button>
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Batal</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompaniesPage;
